<script setup>
import { ref, onMounted } from "vue";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import avatarPorDefecto from "@/assets/img/Evolve/usuario.png";
import { saveToIndexedDB, getFromIndexedDB } from "@/utils/indexedDB";
import Swal from "sweetalert2";
import { useInscripcionStore } from "@/store/inscripcionStore";

import youtubeIcon from "@/assets/img/Evolve/youtube.png";
import facebookIcon from "@/assets/img/Evolve/facebook.png";
import instagramIcon from "@/assets/img/Evolve/instagram.png";
import twitterIcon from "@/assets/img/Evolve/twitter.png";

const emit = defineEmits(["next-step"]);
const store = useInscripcionStore();

const imagePreview = ref(null);
const imageError = ref(null);
const errors = ref({});
const imageInput = ref(null);

const MAX_SIZE_MB = 5;
const allowedExt = ["jpg", "jpeg", "png"];

const opcionesNacionalidades = [
  // América del Norte
  { nombre: "México", codigo_iso: "mx" },
  { nombre: "Estados Unidos", codigo_iso: "us" },
  { nombre: "Canadá", codigo_iso: "ca" },

  // América Central y Caribe
  { nombre: "Guatemala", codigo_iso: "gt" },
  { nombre: "Belice", codigo_iso: "bz" },
  { nombre: "Honduras", codigo_iso: "hn" },
  { nombre: "El Salvador", codigo_iso: "sv" },
  { nombre: "Nicaragua", codigo_iso: "ni" },
  { nombre: "Costa Rica", codigo_iso: "cr" },
  { nombre: "Panamá", codigo_iso: "pa" },
  { nombre: "Cuba", codigo_iso: "cu" },
  { nombre: "República Dominicana", codigo_iso: "do" },
  { nombre: "Haití", codigo_iso: "ht" },
  { nombre: "Puerto Rico", codigo_iso: "pr" },

  // América del Sur
  { nombre: "Colombia", codigo_iso: "co" },
  { nombre: "Venezuela", codigo_iso: "ve" },
  { nombre: "Ecuador", codigo_iso: "ec" },
  { nombre: "Perú", codigo_iso: "pe" },
  { nombre: "Brasil", codigo_iso: "br" },
  { nombre: "Bolivia", codigo_iso: "bo" },
  { nombre: "Paraguay", codigo_iso: "py" },
  { nombre: "Uruguay", codigo_iso: "uy" },
  { nombre: "Argentina", codigo_iso: "ar" },
  { nombre: "Chile", codigo_iso: "cl" },

  // Europa
  { nombre: "España", codigo_iso: "es" },
  { nombre: "Portugal", codigo_iso: "pt" },
  { nombre: "Francia", codigo_iso: "fr" },
  { nombre: "Alemania", codigo_iso: "de" },
  { nombre: "Italia", codigo_iso: "it" },
  { nombre: "Reino Unido", codigo_iso: "gb" },
  { nombre: "Países Bajos", codigo_iso: "nl" },
  { nombre: "Bélgica", codigo_iso: "be" },
  { nombre: "Suiza", codigo_iso: "ch" },
  { nombre: "Austria", codigo_iso: "at" },
  { nombre: "Suecia", codigo_iso: "se" },
  { nombre: "Noruega", codigo_iso: "no" },
  { nombre: "Dinamarca", codigo_iso: "dk" },
  { nombre: "Polonia", codigo_iso: "pl" },
  { nombre: "Rusia", codigo_iso: "ru" },

  // Asia
  { nombre: "Japón", codigo_iso: "jp" },
  { nombre: "China", codigo_iso: "cn" },
  { nombre: "Corea del Sur", codigo_iso: "kr" },
  { nombre: "India", codigo_iso: "in" },
  { nombre: "Tailandia", codigo_iso: "th" },
  { nombre: "Vietnam", codigo_iso: "vn" },
  { nombre: "Filipinas", codigo_iso: "ph" },

  // Otros
  { nombre: "Australia", codigo_iso: "au" },
  { nombre: "Sudáfrica", codigo_iso: "za" },
  { nombre: "Israel", codigo_iso: "il" },
];

const opcionesGenero = [
  { label: "Masculino", value: "H" },
  { label: "Femenino", value: "M" },
];

const iconosRedes = {
  instagram: instagramIcon,
  facebook: facebookIcon,
  youtube: youtubeIcon,
  twitter: twitterIcon,
};

const placeholderRedes = {
  instagram: "https://instagram.com/tu_usuario",
  facebook: "https://facebook.com/tu_perfil",
  youtube: "https://youtube.com/@tu_canal",
  twitter: "https://twitter.com/tu_usuario",
};

onMounted(async () => {
  const file = await getFromIndexedDB("peleadores_evento", "fotoPerfil");
  if (file) {
    store.foto = file;
    imagePreview.value = URL.createObjectURL(file);
  }
});

function triggerImageInput() {
  imageInput.value?.click();
}

async function onImageChange(event) {
  imageError.value = null;
  const file = event.target.files[0];
  if (!file) return;

  const ext = file.name.split(".").pop().toLowerCase();
  const extOk = allowedExt.includes(ext);
  const mimeOk = file.type.startsWith("image/");
  const sizeOk = file.size <= MAX_SIZE_MB * 1024 * 1024;

  if (!extOk || !mimeOk) {
    imageError.value = "Formato no válido. Usa JPG o PNG.";
    return;
  }
  if (!sizeOk) {
    imageError.value = `La imagen supera ${MAX_SIZE_MB}MB.`;
    return;
  }

  store.foto = file;
  imagePreview.value = URL.createObjectURL(file);
  await saveToIndexedDB("peleadores_evento", "fotoPerfil", file);
}

function formatearTelefono(numero) {
  const limpio = numero.replace(/\D/g, "");
  if (limpio.length === 10) return `+52${limpio}`;
  if (limpio.length === 12 && limpio.startsWith("52")) return `+${limpio}`;
  if (limpio.length === 13 && limpio.startsWith("521")) return `+${limpio}`;
  return null;
}

function validar() {
  errors.value = {};
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const tel = formatearTelefono(store.telefono);

  if (!store.foto) errors.value.foto = true;
  if (!store.nombre.trim()) errors.value.nombre = true;
  if (!store.apellido.trim()) errors.value.apellido = true;
  if (!emailRegex.test(store.email)) errors.value.email = true;
  if (!tel) errors.value.telefono = true;
  else store.telefono = tel;
  if (!store.fechaNacimiento) errors.value.fechaNacimiento = true;
  if (!store.genero) errors.value.genero = true;
  if (!store.nacionalidad) errors.value.nacionalidad = true;

  return Object.keys(errors.value).length === 0;
}

function continuar() {
  if (!validar()) {
    Swal.fire({
      icon: "warning",
      title: "Campos incompletos",
      text: "Revisa los campos marcados antes de continuar.",
      confirmButtonColor: "#007bff",
    });
    return;
  }
  emit("next-step");
}
</script>

<template>
  <form class="step-container" @submit.prevent="continuar">
    <h5 class="step-titulo">Datos personales del competidor</h5>

    <!-- ── Foto + Nombre/Apellido en desktop ── -->
    <div class="foto-nombre-row mb-14">
      <!-- Foto izquierda -->
      <div class="foto-lado">
        <div
          class="foto-wrap"
          :class="{ 'foto-error': errors.foto && !imageError }"
          @click="triggerImageInput"
        >
          <img
            :src="imagePreview || avatarPorDefecto"
            alt="Foto de perfil"
            class="foto-img"
          />
          <span class="foto-edit">✎</span>
        </div>
        <input
          ref="imageInput"
          type="file"
          class="d-none"
          accept=".jpg,.jpeg,.png"
          @change="onImageChange"
        />
        <p v-if="imageError" class="error-msg mt-1">{{ imageError }}</p>
        <p v-if="errors.foto && !imageError" class="error-msg mt-1">
          La foto es requerida
        </p>
        <p class="foto-hint">JPG o PNG · máx. {{ MAX_SIZE_MB }}MB</p>
      </div>

      <!-- Nombre y Apellido a la derecha de la foto (solo desktop) -->
      <div class="nombre-apellido-col">
        <div class="field-group">
          <label class="field-label">Nombre <span class="req">*</span></label>
          <input
            type="text"
            class="field-input"
            :class="{ 'field-error': errors.nombre }"
            v-model="store.nombre"
            autocomplete="given-name"
            placeholder="Ej. Carlos"
          />
          <span v-if="errors.nombre" class="error-msg">Requerido</span>
        </div>
        <div class="field-group">
          <label class="field-label">Apellido <span class="req">*</span></label>
          <input
            type="text"
            class="field-input"
            :class="{ 'field-error': errors.apellido }"
            v-model="store.apellido"
            autocomplete="family-name"
            placeholder="Ej. García"
          />
          <span v-if="errors.apellido" class="error-msg">Requerido</span>
        </div>
      </div>
    </div>

    <!-- ── Email · Teléfono ── -->
    <div class="form-row-2 mb-14">
      <div class="field-group">
        <label class="field-label">Correo electrónico <span class="req">*</span></label>
        <input
          type="email"
          class="field-input"
          :class="{ 'field-error': errors.email }"
          v-model="store.email"
          autocomplete="email"
          placeholder="correo@ejemplo.com"
        />
        <span v-if="errors.email" class="error-msg">Correo no válido</span>
      </div>
      <div class="field-group">
        <label class="field-label">Teléfono <span class="req">*</span></label>
        <input
          type="tel"
          class="field-input"
          :class="{ 'field-error': errors.telefono }"
          v-model="store.telefono"
          autocomplete="tel"
          placeholder="10 dígitos"
        />
        <span v-if="errors.telefono" class="error-msg"
          >Teléfono no válido (10 dígitos)</span
        >
      </div>
    </div>

    <!-- ── Fecha nacimiento · Género · Nacionalidad ── -->
    <div class="form-row-3 mb-14">
      <div class="field-group">
        <label class="field-label">Fecha de nacimiento <span class="req">*</span></label>
        <input
          type="date"
          class="field-input"
          :class="{ 'field-error': errors.fechaNacimiento }"
          v-model="store.fechaNacimiento"
        />
        <span v-if="errors.fechaNacimiento" class="error-msg">Requerido</span>
      </div>
      <div class="field-group">
        <label class="field-label">Género <span class="req">*</span></label>
        <v-select
          v-model="store.genero"
          :options="opcionesGenero"
          :reduce="(o) => o.value"
          label="label"
          placeholder="Selecciona"
          :clearable="false"
          :class="{ 'vs-error': errors.genero }"
        />
        <span v-if="errors.genero" class="error-msg">Requerido</span>
      </div>
      <div class="field-group">
        <label class="field-label">Nacionalidad <span class="req">*</span></label>
        <v-select
          v-model="store.nacionalidad"
          :options="opcionesNacionalidades"
          :reduce="(o) => o.codigo_iso"
          label="nombre"
          placeholder="País"
          :clearable="false"
          class="select-nacionalidad"
          :class="{ 'vs-error': errors.nacionalidad }"
        />
        <span v-if="errors.nacionalidad" class="error-msg">Requerido</span>
      </div>
    </div>

    <!-- ── Ciudad / Estado (opcional) ── -->
    <div class="field-group mb-14">
      <label class="field-label">
        Ciudad / Estado <span class="field-opt">(opcional)</span>
      </label>
      <input
        type="text"
        class="field-input"
        v-model="store.direccion"
        placeholder="Ej. Saltillo, Coahuila"
      />
    </div>

    <!-- ── Redes sociales (opcional) ── -->
    <div class="field-group mb-14">
      <label class="field-label">
        Redes sociales <span class="field-opt">(opcional)</span>
      </label>
      <div class="redes-grid">
        <div
          v-for="red in ['instagram', 'facebook', 'youtube', 'twitter']"
          :key="red"
          class="red-item"
        >
          <img :src="iconosRedes[red]" :alt="red" class="red-icon" />
          <input
            type="url"
            class="field-input"
            v-model="store.redes_sociales[red]"
            :placeholder="placeholderRedes[red]"
          />
        </div>
      </div>
    </div>

    <!-- ── Navegación ── -->
    <div class="nav-row">
      <a href="/" class="btn-nav btn-secondary-nav">
        <i class="bi bi-house-fill"></i> Inicio
      </a>
      <button type="submit" class="btn-nav btn-primary-nav">
        Siguiente <i class="bi bi-arrow-right"></i>
      </button>
    </div>
  </form>
</template>

<style scoped>
.step-container {
  padding: 0.5rem 0;
}
.step-titulo {
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a2e;
  text-align: center;
  margin-bottom: 1.5rem;
}
.mb-14 {
  margin-bottom: 14px;
}

/* ── Grid helpers ── */
.form-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.form-row-3 {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 14px;
}

/* ── Campos ── */
.field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.field-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #444;
}
.field-opt {
  font-weight: 400;
  color: #aaa;
  font-size: 0.75rem;
}
.req {
  color: #dc3545;
}
.field-input {
  width: 100%;
  padding: 8px 12px;
  border: 1.5px solid #d8d8d8;
  border-radius: 8px;
  font-size: 0.88rem;
  color: #333;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  font-family: inherit;
  background: #fff;
}
.field-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.12);
}
.field-error {
  border-color: #dc3545 !important;
}
.error-msg {
  font-size: 0.71rem;
  color: #dc3545;
}
:deep(.vs-error .vs__dropdown-toggle) {
  border-color: #dc3545 !important;
}
/* ✅ Nacionalidad ya tiene ~50 países: mostramos solo ~5 opciones a la vez
   y el resto se navega con scroll (vue-select ya trae buscador integrado
   para saltar directo escribiendo el país). */
:deep(.select-nacionalidad .vs__dropdown-menu) {
  max-height: 210px;
  overflow-y: auto;
}

/* ── Foto + nombre/apellido row ── */
.foto-nombre-row {
  display: flex;
  align-items: flex-start;
  gap: 24px;
}

/* Columna izquierda: foto */
.foto-lado {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-shrink: 0;
}

/* Columna derecha: nombre + apellido apilados */
.nombre-apellido-col {
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
  /* alinea verticalmente con la foto */
  justify-content: center;
  align-self: center;
}

/* ── Foto ── */
.foto-wrap {
  position: relative;
  width: 140px;
  height: 140px;
  border-radius: 16px; /* bordes redondeados, no círculo */
  overflow: hidden;
  cursor: pointer;
  border: 3px solid #d8d8d8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: border-color 0.2s;
}
.foto-wrap:hover {
  border-color: #007bff;
}
.foto-error {
  border-color: #dc3545 !important;
}
.foto-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}
.foto-wrap:hover .foto-img {
  transform: scale(1.05);
}
.foto-edit {
  position: absolute;
  bottom: 6px;
  right: 6px;
  background: #fff;
  border-radius: 50%;
  padding: 4px 7px;
  font-size: 13px;
  border: 1px solid #ccc;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}
.foto-hint {
  font-size: 0.72rem;
  color: #aaa;
  margin: 4px 0 0;
}

/* ── Redes: 2 columnas en desktop ── */
.redes-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.red-item {
  display: flex;
  align-items: center;
  gap: 6px;
}
.red-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
  flex-shrink: 0;
}

/* ── Navegación ── */
.nav-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}
.btn-nav {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 20px;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  text-decoration: none;
  transition: all 0.2s;
}
.btn-primary-nav {
  background: #007bff;
  color: #fff;
}
.btn-primary-nav:hover {
  background: #0060df;
}
.btn-secondary-nav {
  background: #f0f0f0;
  color: #555;
}
.btn-secondary-nav:hover {
  background: #e0e0e0;
}

/* ── Responsive ── */
@media (max-width: 700px) {
  .form-row-3 {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 480px) {
  /* Foto vuelve a centrarse y nombre/apellido bajan debajo */
  .foto-nombre-row {
    flex-direction: column;
    align-items: flex-start;
  }
  .foto-lado {
    align-items: flex-start;
  }
  .foto-wrap {
    width: 110px;
    height: 110px;
    border-radius: 12px;
  }
  .nombre-apellido-col {
    width: 100%;
  }
  .form-row-2,
  .form-row-3 {
    grid-template-columns: 1fr;
  }
  /* Redes: 1 por renglón en móvil */
  .redes-grid {
    grid-template-columns: 1fr;
  }
}
</style>
